'use client';

import { useCallback, useState } from 'react';
import { toast } from 'sonner';
import { ApiError, transactionsApi } from '@/lib/api';
import { useAuthedFetcher } from './swrConfig';

/**
 * "Isso parece errado" — reports a wrong merchant identity for a transaction (VER-330's
 * endpoint, surfaced by the VER-358 geo-contradiction band). Fire-and-forget from the
 * user's view: a toast confirms, and `flaggedIds` keeps any already-flagged transaction
 * from offering the action twice in the same session — a SET, because the drawer swaps
 * transactions in place (counterpart navigation) without unmounting this hook.
 */
export function useFlagMerchant(): {
  flagMerchant: (transactionId: string) => Promise<void>;
  isFlagging: boolean;
  flaggedIds: ReadonlySet<string>;
} {
  const authedFetch = useAuthedFetcher();
  const [isFlagging, setIsFlagging] = useState(false);
  const [flaggedIds, setFlaggedIds] = useState<ReadonlySet<string>>(new Set());

  const flagMerchant = useCallback(
    async (transactionId: string) => {
      setIsFlagging(true);
      try {
        await authedFetch((token) => transactionsApi.flagMerchant(token, transactionId));
        setFlaggedIds((prev) => new Set(prev).add(transactionId));
        toast.success('Obrigado! Vamos revisar esse estabelecimento.');
      } catch (err) {
        // A 402 already surfaced the global upgrade dialog inside useAuthedFetcher —
        // adding a generic failure toast on top would be a misleading double message.
        if (!(err instanceof ApiError && err.isLimitExceeded())) {
          toast.error('Não foi possível enviar. Tente novamente.');
        }
      } finally {
        setIsFlagging(false);
      }
    },
    [authedFetch],
  );

  return { flagMerchant, isFlagging, flaggedIds };
}
